import { useRef, forwardRef, useImperativeHandle } from 'react';
import Slider from 'react-slick';

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import './VideoSlider.css';

const parentWithClass = (element, className) => {
  while (element && !element.classList.contains(className)) {
    element = element.parentElement;
  }
  return element;
}

const getSlickSlideIndex = (element) => {
  const slickSlide = parentWithClass(element, 'slick-slide');
  return slickSlide ? slickSlide.getAttribute('data-index') : null;
}

function videoOnPlay(event) {
  const videos = document.querySelectorAll('video:not([autoplay])');
  for (let i = 0; i < videos.length; i++) {
    if (videos[i] !== event.target) {
      videos[i].pause();
      videos[i].currentTime = 0;
    }
  }
}

const VideoSlider = forwardRef(({ srcs, allVideosRef }, ref) => {
  const sliderRef = useRef(null);
  const videoRefs = useRef([]);

  const getVideo = (index) => {
    if (!sliderRef.current || videoRefs.current.length === 0) return null;

    let offset = 0;
    if (sliderRef.current) {
      if (sliderRef.current.innerSlider.props.rows > 1)
        return null;
      offset = Math.floor((sliderRef.current.innerSlider.props.slidesToShow - 1) / 2);
    }


    const videoWithClones = videoRefs.current[(index + offset) % videoRefs.current.length];
    for (const video of videoWithClones) {
      if (getSlickSlideIndex(video) === index + offset) {
        return video;
      }
    }

    // This should not be reached.
    return null;
  }

  // Pause all videos
  const pause = () => {
    for (const video of videoRefs.current.flat()) {
      if (video) {
        video.pause();
        video.currentTime = 0;
      }
    }
  }

  // Pause all videos and play the current one
  const play = () => {
    if (!sliderRef.current) return;
    const current = sliderRef.current.innerSlider.state.currentSlide;
    const video = getVideo(current);
    if (video) {
      pause();
      video.play();
    }
  }

  useImperativeHandle(ref, () => ({
    pause,
    play
  }));

  const beforeChange = (current) => {
    const video = getVideo(current);
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };
  const afterChange = (current) => {
    const video = getVideo(current);
    if (video) {
      video.play();
    }
  };

  const settings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    rows: srcs.length > 6 ? 2 : 1,
    swipeToSlide: true,
    beforeChange,
    afterChange,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 512,
        settings: {
          rows: 1,
          slidesToShow: 1,
        }
      }
    ]
  };

  return (
    <Slider ref={sliderRef} {...settings}>
      {srcs.map((src, index) => (
        <div className='video-container' key={index}>
          <video
            src={src} controls playsInline
            onPlay={videoOnPlay}
          />
        </div>
      ))}
    </Slider>
  );
});

export default VideoSlider;
